import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaLink, FaRegEye, FaUsers } from "react-icons/fa";
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import axios from "axios";
import { toast } from "react-toastify";
import { MdInventory } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import { BsThreeDotsVertical } from "react-icons/bs";
import SearchInput from "../../../utils/SearchInput";
import TableLoader from "../../../utils/TableLoader";
import PaginationComponent from "../../../utils/PaginationComponent";
import { TbEdit } from "react-icons/tb";
import { RiRestaurantFill } from "react-icons/ri";
import { BiSolidTrashAlt } from "react-icons/bi";
import RestaurantView from "./RestaurantView";
import RestaurantEdit from "./RestaurantEdit";

const RestaurantManagement = ((props, ref) => {
  const { hasPermission } = props;

  // Navigate
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");

  // State Management: Data & Pagination
  const [restroData, setRestroData] = useState([]);
  const [isLoading, setIsLoading] = useState({ main: false, edit: false });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State Management: Edit/View Mode
  const [currentRestroData, setCurrentRestroData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [userName, setUserName] = useState("");
  const [surName, setSurName] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [email, setEmail] = useState("");
  const [collaborationDescription, setCollaborationDescription] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [logo, setLogo] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoError, setLogoError] = useState('');
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;

  const [searchText, setSearchText] = useState("");

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const changeRestaurantStatus = async (restID, currentStatus) => {
    setIsLoading({ ...isLoading, main: true });
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const response = await axios.put(
        `${apiUrl}/api/v1/restaurants/${restID}/status`,
        { status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        getRestroData();
      } else {
        console.error("Failed to update status:", response.statusText);
      }
    } catch (error) {
      console.error("Error updating restaurant status:", error);
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  // Getting Restaurants Data From API
  const getRestroData = async () => {
    setIsLoading({ ...isLoading, main: true });
    try {
      let params;
      if (searchText) {
        params = {
          pageNumber: page,
          size: rowsPerPage,
          search: searchText,
        };
      } else {
        params = {
          pageNumber: page,
          size: rowsPerPage,
        };
      }
      const response = await axios.get(`${apiUrl}/api/v1/restaurants`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data;
      setRestroData(data.data);
      setTotalPages(data.total);
    } catch (error) {
      console.error('Error fetching wine data:', error);
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  // Edit Handlers
  const handleEdit = (restro) => {
    setIsEditing(true);
    setIsViewing(false);
    setCurrentRestroData(restro);
  };

  // View Handlers
  const handleView = (restro) => {
    setIsViewing(true);
    setIsEditing(false);
    setCurrentRestroData(restro);
  };

  // Cancel Edit/View Mode
  const handleCancel = () => {
    setIsEditing(false);
    setIsViewing(false);
    setCurrentRestroData(null);
  };

  // Save Restaurant Data after Editing
  const handleSave = async () => {
    // {
    //   username: userName,
    //   surname: surName,
    //   restaurant_name: restaurantName,
    //   collaboration_description: collaborationDescription,
    //   mobile_phone: mobilePhone,
    //   company_name: companyName,
    // },
    if (!logoError) {
      const formData = new FormData();

      formData.append('username', userName);
      formData.append('surname', surName);
      formData.append('restaurant_name', restaurantName);
      formData.append('collaboration_description', collaborationDescription);
      formData.append('mobilePhone', mobilePhone);
      formData.append('companyName', companyName);
      formData.append('_method', 'put');

      if (selectedLogo) {
        formData.append('logo', selectedLogo);
      }
      try {
        setIsLoading({ ...isLoading, edit: true });
        const response = await axios.post(
          `${apiUrl}/api/v1/restaurants/${currentRestroData?.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success(response?.message || 'Restaurant updated successfully!', {
          theme: 'colored',
          autoClose: 1000,
        });
        setIsEditing(false);
        getRestroData();
      } catch (error) {
        console.error('Error Editing user:', error?.response?.data?.errors);
        const errors = error?.response?.data?.errors;
        if (errors) {
          for (const key in errors) {
            if (Array.isArray(errors[key])) {
              errors[key].forEach((message) => {
                toast.error(message, {
                  theme: 'colored',
                  autoClose: 3000,
                });
              });
            }
          }
        } else {
          toast.error('Something went wrong. Please try again.', {
            theme: 'colored',
            autoClose: 3000,
          });
        }
      } finally {
        setIsLoading({ ...isLoading, edit: false });
      }
    } else {
      toast.error(logoError, { theme: "colored", autoClose: 1000 });
    }
  };

  // Delete Restaurant Data
  // const handleDelete =()=>{

  // }



  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setLogoError('No file selected');
      return;
    }

    // 1. FILE SIZE CHECK (2MB max)
    // const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
    // if (file.size > maxSizeInBytes) {
    //     setLogoError("Max size: 2MB. Compress your image first.");
    //     return;
    // }

    // 2. STRICT DIMENSION VALIDATION (must be exactly 249×94)
    const img = new Image();
    img.onload = () => {
      // const REQUIRED_WIDTH = 249;
      // const REQUIRED_HEIGHT = 94;

      // if (img.width !== REQUIRED_WIDTH || img.height !== REQUIRED_HEIGHT) {
      //     setLogoError(`Image must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT}px.`);
      //     return;
      // }

      // Validation passed
      setLogoError('');
      setLogo(URL.createObjectURL(file));
      setSelectedLogo(file);
    };

    img.onerror = () => {
      setLogoError('Invalid image file');
    };

    img.src = URL.createObjectURL(file);
  };


  useImperativeHandle(ref, () => ({
    restaurant() {
      setIsEditing(false);
      setIsViewing(false);
      setCurrentRestroData(null);
    },
  }));

  useEffect(() => {
    if (currentRestroData) {
      setUserName(currentRestroData?.username);
      setSurName(currentRestroData?.surname);
      setRestaurantName(currentRestroData?.restaurant_name);
      setEmail(currentRestroData?.email);
      setCollaborationDescription(currentRestroData?.collaboration_description);
      setMobilePhone(currentRestroData?.mobile_phone);
      setCompanyName(currentRestroData?.company_name);
      setLogo(currentRestroData?.logo ? `${logoUrl}/${currentRestroData?.logo}` : null);
    }
  }, [currentRestroData]);

  useEffect(() => {
    getRestroData();
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    const handlePageRefresh = () => {
      localStorage.removeItem("searchText");
    };

    window.addEventListener("beforeunload", handlePageRefresh);

    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, [])

  return (
    <>
      {!isEditing && !isViewing ? (
        <section className="border-t border-[#D9D9D9] sm:mt-5 pt-8">
          <div className="flex sm:flex-row flex-col justify-between items-center lg:mb-4 mb-5">
            <h3 className="md:text-3xl self-start	 text-lg font-bold mb-2 sm:mb-0 poppins-medium">Lista Ristoranti</h3>
            <div className="flex items-center space-x-4 sm:w-fit w-full">
              <SearchInput
                onSearchChange={handleSearchChange}
                component="Restaurant Management"
                placeholder="Ristoranti"
              />
              {/* <button
              className="flex items-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] hover:text-[#8A2533] hover:bg-[#f6efef] transition-colors group"
              onClick={() => navigate("/admin/restaurant-add")}
            >
              <img
                src={plus}
                alt="Add"
                className="w-3 h-3 filter invert-0 transition duration-300 ease-in-out group-hover:invert"
              />
              <span className="ml-2 poppins-regular">Aggiungi</span>
            </button> */}
            </div>
          </div>
          <div className="shadow-scroll-x overflow-x-scroll relative user-mangement-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow">
            <div className="">
              <table className="lg:w-full md:w-[600px] text-left wine_table">
                <thead>
                  <tr className="border-b border-[#8B2433]">
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Codice</th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome Utente</th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome Ristorante</th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Ragione Sociale</th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B] w-[150px]">Cellulare</th>
                    <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Email</th>
                    {hasPermission("Restaurants", "edit") && (
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Stato</th>
                    )}
                    <th className="pb-3 px-7 text-left poppins-medium text-[#6B6B6B] w-[150px]">Opzioni</th>
                  </tr>
                </thead>
                {isLoading?.main ? (
                  <TableLoader />
                ) : (
                  <tbody>
                    {restroData?.length ? (
                      restroData?.map((restaurant, index) => (
                        <tr key={index} className="border-b border-[#8B2433]/12">
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.code ? restaurant?.code : "-"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.username ? restaurant?.username : "-"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.restaurant_name ? restaurant?.restaurant_name : "-"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.company_name ? restaurant?.company_name : "-"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.mobile_phone ? restaurant?.mobile_phone : "-"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{restaurant?.email ? restaurant?.email : "-"}</td>
                          {hasPermission("Restaurants", "edit") && (
                            <td className="py-4 px-2">
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  value=""
                                  checked={restaurant?.status === "Active" ? true : false}
                                  onChange={() => {
                                    changeRestaurantStatus(restaurant?.id, restaurant?.status)
                                  }}
                                  className="sr-only peer"
                                />
                                <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:bg-[#8B2433]"></div>
                              </label>
                            </td>
                          )}
                          <td className="py-4 px-7">
                            <div className="text-red-700 gap-4 flex items-center justify-start">
                              <button onClick={() => navigate(`/restaurant/${restaurant?.slug}`)}>
                                <FaLink color="#8B2433" size={"18px"} />
                              </button>
                              {hasPermission("WineList", "view") && (
                                <button onClick={() => navigate(`/admin/restaurantManagement/${restaurant?.id}`)}>
                                  <FaPlus color="#8B2433" size={"22px"} />
                                </button>
                              )}
                              <Menu>
                                <MenuButton><BsThreeDotsVertical color="#8B2433" size={"18px"} /></MenuButton>
                                <MenuItems anchor="bottom" className="bg-[#fffbfb] w-[140px] border text-black py-0 flex flex-col  shadow-2xl rounded-xl relative focus-visible:outline-none overflow-hidden">
                                  {/* Arrow at the top */}
                                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>

                                  {hasPermission("Restaurants", "view") && (
                                    <MenuItem className="flex gap-2 justify-start items-center poppins-regular px-3 pe-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => handleView(restaurant)}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <FaRegEye size={"18px"} className="flex-shrink-0" />
                                        <span className="truncate">Visualizza</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("Restaurants", "edit") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => handleEdit(restaurant)}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <TbEdit size={"18px"} className="flex-shrink-0" />
                                        <span className="truncate">Modifica</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("WineStock", "view") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => navigate(`/admin/wineStock/${restaurant?.id}`)}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <MdInventory size={"18px"} className="flex-shrink-0" />
                                        <span className="truncate">Cantina</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {["Super Admin", "Sales Person"].includes(localStorage.getItem("RoleName")) && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => {
                                          localStorage.setItem('currentRestId', restaurant?.id);
                                          localStorage.setItem('accessToAdmin', true);
                                          navigate(`/restaurantPanel/dashboard/${restaurant?.id}`)
                                        }}
                                        className="w-full text-left flex gap-2 items-center overflow-hidden text-ellipsis"
                                      >
                                        <RiRestaurantFill size={"18px"} className="flex-shrink-0" />
                                        <span>Console Ristorante</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("Users", "view") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button
                                        onClick={() => {
                                          localStorage.setItem('currentRestId', restaurant?.id)
                                          localStorage.setItem('accessToAdmin', true);
                                          navigate(`/restaurantPanel/restaurant-user/${restaurant?.id}`)
                                        }}
                                        className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis"
                                      >
                                        <FaUsers size={"18px"} className="flex-shrink-0" />
                                        <span className="truncate">Utenti</span>
                                      </button>
                                    </MenuItem>
                                  )}

                                  {hasPermission("Restaurants", "remove") && (
                                    <MenuItem className="flex gap-2 justify-start border-t items-center poppins-regular px-3 py-2 hover:bg-[#8b2532] hover:text-[#fff] transition-all duration-300 ease-in-out">
                                      <button className="w-full text-left flex gap-2 items-center whitespace-nowrap overflow-hidden text-ellipsis">
                                        <BiSolidTrashAlt size={"18px"} className="flex-shrink-0" />
                                        <span className="truncate">Elimina</span>
                                      </button>
                                    </MenuItem>
                                  )}
                                </MenuItems>
                              </Menu>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-4">No restaurants found</td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </div>

          </div>
          {restroData && restroData?.length > 0 && (
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </section>
      ) : isViewing ? (
        <RestaurantView currentRestroData={currentRestroData} handleCancel={handleCancel} />
      ) : (
        <RestaurantEdit
          isLoading={isLoading}
          userName={userName}
          surName={surName}
          restaurantName={restaurantName}
          email={email}
          collaborationDescription={collaborationDescription}
          companyName={companyName}
          mobilePhone={mobilePhone}
          logo={logo}
          logoError={logoError}
          setUserName={setUserName}
          setSurName={setSurName}
          setRestaurantName={setRestaurantName}
          setEmail={setEmail}
          setCollaborationDescription={setCollaborationDescription}
          setMobilePhone={setMobilePhone}
          setCompanyName={setCompanyName}
          handleLogoChange={handleLogoChange}
          handleView={handleView}
          handleSave={handleSave}
          handleCancel={handleCancel}
        />
      )}
    </>
  );
});

export default forwardRef(RestaurantManagement);